/**************************************
 * Product Drawer / Quick View
 **************************************/

(() => {
  const QUICK_VIEW_SELECTOR = '#product-drawer';
  const QUICK_VIEW_CACHE = new Map();

  /**
   * -----------------------------------
   * DOM helpers
   * -----------------------------------
   */

  function getDrawerElements() {
    const drawer = document.querySelector(QUICK_VIEW_SELECTOR);

    return {
      drawer,
      panel: drawer?.querySelector('.drawer-panel') || null,
      content: drawer?.querySelector('.drawer-content') || null
    };
  }

  function setDrawerLoading(content) {
    if (!content) return;

content.innerHTML = `
    <div class="qv-loading" role="status" aria-label="Loading product">
      <div class="qv-loading__media">
        <div class="qv-skeleton qv-skeleton--image"></div>
        <div class="qv-skeleton qv-skeleton--image"></div>
      </div>

      <div class="qv-loading__info">
        <div class="qv-skeleton qv-skeleton--title"></div>
        <div class="qv-skeleton qv-skeleton--price"></div>

        <div class="qv-loading__swatches">
          <span class="qv-skeleton qv-skeleton--circle"></span>
          <span class="qv-skeleton qv-skeleton--circle"></span>
          <span class="qv-skeleton qv-skeleton--circle"></span>
          <span class="qv-skeleton qv-skeleton--circle"></span>
        </div>

        <div class="qv-skeleton qv-skeleton--label"></div>

        <div class="qv-loading__sizes">
          <span class="qv-skeleton qv-skeleton--size"></span>
          <span class="qv-skeleton qv-skeleton--size"></span>
          <span class="qv-skeleton qv-skeleton--size"></span>
          <span class="qv-skeleton qv-skeleton--size"></span>
        </div>

        <div class="qv-skeleton qv-skeleton--button"></div>
      </div>
    </div>
  `;
  }

  function setDrawerError(content) {
    if (!content) return;

    content.innerHTML = `
      <div class="drawer-error" role="alert">
        Error loading product
      </div>
    `;
  }

  /**
   * -----------------------------------
   * Open / close drawer
   * -----------------------------------
   */

  function showQuickViewDrawer() {
    const { drawer } = getDrawerElements();
    if (!drawer) return;

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');

    document.body.classList.add('drawer-open');
  }

  function closeQuickView() {
    const { drawer } = getDrawerElements();
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');

    document.body.classList.remove('drawer-open');

    drawer.dispatchEvent(
      new CustomEvent('quickview:closed', { bubbles: true })
    );

    delete drawer.dataset.mode;
    delete drawer.dataset.confirmLabel;
  }

  /*
   * The theme editor captures clicks on submit buttons inside /cart/add forms
   * and opens the cart page. The studio base picker only needs the chosen variant.
   */

  function neutralizeStudioBaseForm(content) {
    const drawer = content.closest(QUICK_VIEW_SELECTOR);
    if (!drawer || drawer.dataset.mode !== 'studio-base-variant') return;

    for (const form of content.querySelectorAll('form')) {
      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) continue;

      form.dataset.studioBaseForm = 'true';
      form.dataset.studioOriginalAction = action;
      form.setAttribute('action', '#');

      for (const button of form.querySelectorAll('button[type="submit"], input[type="submit"]')) {
        button.type = 'button';
        button.removeAttribute('name');
        button.removeAttribute('data-add-to-cart');
        button.setAttribute('data-studio-confirm-variant', 'true');
      }
    }
  }

  /**
   * -----------------------------------
   * Fetch product view
   * -----------------------------------
   */

  async function fetchProductView(handle, view = 'quick-view') {
    const cacheKey = `${handle}:${view}`;

    if (QUICK_VIEW_CACHE.has(cacheKey)) {
      return QUICK_VIEW_CACHE.get(cacheKey);
    }

    const url = `/products/${encodeURIComponent(handle)}?view=${encodeURIComponent(view)}`;
    const response = await fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(
        `Unable to load product view: ${response.status}`
      );
    }

    const html = await response.text();

    QUICK_VIEW_CACHE.set(cacheKey, html);

    return html;
  }

  /**
   * -----------------------------------
   * Money
   * -----------------------------------
   */

  function formatMoney(cents) {
    if (
      window.theme?.Currency &&
      typeof window.theme.Currency.formatMoney === 'function'
    ) {
      return window.theme.Currency.formatMoney(
        cents,
        window.theme.settings?.moneyFormat
      );
    }

    if (
      window.Shopify &&
      typeof window.Shopify.formatMoney === 'function'
    ) {
      return window.Shopify.formatMoney(cents);
    }

    return `${(Number(cents) / 100).toFixed(2)}`;
  }

  /**
   * -----------------------------------
   * Variant controller
   * -----------------------------------
   */

  function initQuickViewVariants(content) {
    const section = content.querySelector(
      '[data-section-type="product"]'
    );

    const jsonElement = content.querySelector(
      '.quick-view-product-json'
    );

    if (!section || !jsonElement) return;

    let product;

    try {
      product = JSON.parse(jsonElement.textContent);
    } catch (error) {
      console.error('Invalid quick-view product JSON:', error);
      return;
    }

    const form = section.querySelector(
      'form[data-studio-base-form="true"], form[action*="/cart/add"]'
    );

    const variantIdInput = form?.querySelector('[name="id"]');
    const addButton = form?.querySelector(
      '[data-studio-confirm-variant], [type="submit"]'
    );
    

    const priceElement = section.querySelector(
      '[data-product-price]'
    );

    const comparePriceElement = section.querySelector(
      '[data-compare-price]'
    );

    const comparePriceWrapper = section.querySelector(
      '[data-product-price-wrap]'
    );

    const optionInputs = Array.from(
      section.querySelectorAll(
        'input[type="radio"][data-variant-input], ' +
        'input[type="radio"][name]'
      )
    );

    /*
     * Shopify product.options:
     * ["Color", "Size", ...]
     *
     * Option Lable
     */

    const optionNames = Array.isArray(product.options)
      ? product.options
      : [];

    const sizeLabelElement = section.querySelector('[data-size-label]');

    function getOptionPosition(optionName) {
      const normalizedName = String(optionName)
        .trim()
        .toLowerCase();

      return optionNames.findIndex((name) => {
        return String(name).trim().toLowerCase() === normalizedName;
      });
    }

    function updateSizeLabel(variant) {
      if (!variant || !sizeLabelElement) return;

      const sizePosition = optionNames.findIndex((name) => {
        return String(name).trim().toLowerCase() === 'size';
      });

      if (sizePosition < 0) return;

      const selectedSize = variant.options?.[sizePosition];

      if (selectedSize) {
        sizeLabelElement.textContent = selectedSize;
      }
    }

    function getSelectedOptions() {
      const selectedOptions = new Array(optionNames.length).fill(null);

      const checkedInputs = section.querySelectorAll(
        'input[type="radio"]:checked'
      );

      checkedInputs.forEach((input) => {
        const optionPosition = getOptionPosition(input.name);

        if (optionPosition >= 0) {
          selectedOptions[optionPosition] = input.value;
        }
      });

      return selectedOptions;
    }



    function variantMatchesOptions(variant, selectedOptions) {
      const variantOptions = Array.isArray(variant.options)
        ? variant.options
        : [
            variant.option1,
            variant.option2,
            variant.option3
          ];

      return selectedOptions.every((selectedValue, index) => {
        return (
          selectedValue == null ||
          variantOptions[index] === selectedValue
        );
      });
    }

    function findVariant(selectedOptions) {
      return product.variants.find((variant) => {
        return variantMatchesOptions(variant, selectedOptions);
      });
    }

    function findVariantForInput(input) {
      const selectedOptions = getSelectedOptions();
      const optionPosition = getOptionPosition(input.name);

      if (optionPosition >= 0) {
        selectedOptions[optionPosition] = input.value;
      }

      return findVariant(selectedOptions);
    }

    function getInputLabel(input) {
      const escapedId =
        window.CSS?.escape
          ? window.CSS.escape(input.id)
          : input.id;

      return (
        input.closest('.variant-input')?.querySelector('label') ||
        section.querySelector(`label[for="${escapedId}"]`)
      );
    }

    function updateSoldOutStates() {
      optionInputs.forEach((input) => {
        const inputWrapper =
          input.closest('.variant-input') ||
          input.parentElement;

        const label = getInputLabel(input);
        const possibleVariant = findVariantForInput(input);

        const isAvailable = Boolean(
          possibleVariant && possibleVariant.available
        );

        input.disabled = !isAvailable;

        inputWrapper?.classList.toggle(
          'is-soldout',
          !isAvailable
        );

        label?.classList.toggle(
          'is-disabled',
          !isAvailable
        );

        label?.classList.toggle(
          'disabled',
          !isAvailable
        );

        input.setAttribute(
          'aria-disabled',
          String(!isAvailable)
        );

        if (!isAvailable && input.checked) {
          input.checked = false;
        }
      });
    }

    function updateAddButton(variant) {
      if (!addButton) return;

      if (!variant) {
        addButton.disabled = true;
        addButton.textContent = 'Unavailable';
        return;
      }

      addButton.disabled = !variant.available;

      const drawer = content.closest(QUICK_VIEW_SELECTOR);
      const availableLabel = drawer?.dataset.confirmLabel || 'Add to Cart';

      addButton.textContent = variant.available
        ? availableLabel
        : 'Sold Out';
    }

    function updatePrice(variant) {
      if (!variant) return;

      if (priceElement) {
        priceElement.innerHTML = formatMoney(variant.price);
      }

      if (!comparePriceElement || !comparePriceWrapper) return;

      const isOnSale =
        variant.compare_at_price &&
        variant.compare_at_price > variant.price;

      if (isOnSale) {
        comparePriceElement.innerHTML = formatMoney(
          variant.compare_at_price
        );

        comparePriceWrapper.classList.remove('hide');
      } else {
        comparePriceElement.innerHTML = '';
        comparePriceWrapper.classList.add('hide');
      }
    }

 
    function updateVariantId(variant) {
      if (!variantIdInput) return;

      variantIdInput.value = variant?.id || '';

      variantIdInput.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );
    }

    function updateVariant() {
      updateSoldOutStates();

      const selectedOptions = getSelectedOptions();
      const variant = findVariant(selectedOptions);

      updateVariantId(variant);
      updatePrice(variant);
      updateSizeLabel(variant);
      updateAddButton(variant);
      
    }

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        window.requestAnimationFrame(updateVariant);
      });
    });

    updateVariant();

    content.dispatchEvent(
      new CustomEvent('quickview:ready', {
        bubbles: true,
        detail: { product }
      })
    );
  }

  /**
   * -----------------------------------
   * Initialize fetched Quick View
   * -----------------------------------
   */

  function initQuickView(content) {
    initQuickViewVariants(content);

    if (
      window.Shopify &&
      window.Shopify.PaymentButton
    ) {
      try {
        window.Shopify.PaymentButton.init();
      } catch (error) {
        // Dynamic payment button may already be initialized.
      }
    }

    if (window.AOS) {
      window.AOS.refreshHard();
    }
  }

  /**
   * -----------------------------------
   * Open Quick View
   * -----------------------------------
   */

  async function openQuickView(handle, triggerButton = null) {
    const { drawer, content } = getDrawerElements();

    if (!drawer || !content || !handle) return;

    if (triggerButton?.classList.contains('is-loading')) {
      return;
    }

    triggerButton?.classList.add('is-loading');
    triggerButton?.setAttribute('aria-busy', 'true');

    setDrawerLoading(content);
    showQuickViewDrawer();

    try {
      const html = await fetchProductView(
        handle,
        'quick-view'
      );

      content.innerHTML = html;
      neutralizeStudioBaseForm(content);

      initQuickView(content);
    } catch (error) {
      console.error(error);
      setDrawerError(content);

      drawer.dispatchEvent(
        new CustomEvent('quickview:closed', { bubbles: true })
      );

      delete drawer.dataset.mode;
      delete drawer.dataset.confirmLabel;
    } finally {
      triggerButton?.classList.remove('is-loading');
      triggerButton?.removeAttribute('aria-busy');
    }
  }

  /**
   * -----------------------------------
   * Add Jibbitz
   * -----------------------------------
   */

  async function addJibbitz(button) {
    const variantId = button.dataset.variantId;

    if (!variantId) return;
    if (button.classList.contains('is-loading')) return;

    const gridProduct = button.closest('.grid-product');

    const productImage =
      gridProduct?.querySelector('.grid-product__image img') ||
      gridProduct?.querySelector('.grid-product__image') ||
      gridProduct?.querySelector('img');

    button.classList.add('is-loading');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1
            }
          ]
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        throw new Error(
          errorData.description ||
          errorData.message ||
          'Unable to add item to cart'
        );
      }

      await response.json();

      flyToCart(productImage);

      document.dispatchEvent(
        new CustomEvent('cart:build')
      );

      document.dispatchEvent(
        new CustomEvent('cart:item-added', {
          detail: {
            variantId: Number(variantId),
            source: 'jibbitz'
          }
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * -----------------------------------
   * Fly to cart animation
   * -----------------------------------
   */

  function flyToCart(imageElement) {
    const cartIcon = document.querySelector('.cart-link');

    if (!cartIcon || !imageElement) return;

    const image =
      imageElement.tagName === 'IMG'
        ? imageElement
        : imageElement.querySelector('img');

    if (!image) return;

    const imageSource =
      image.currentSrc ||
      image.src ||
      image.dataset.src;

    if (!imageSource) return;

    const imageRect = image.getBoundingClientRect();
    const cartRect = cartIcon.getBoundingClientRect();

    const flyingWrapper = document.createElement('div');
    flyingWrapper.className = 'fly-circle';

    const flyingImage = document.createElement('img');
    flyingImage.src = imageSource;
    flyingImage.alt = '';

    flyingWrapper.appendChild(flyingImage);
    document.body.appendChild(flyingWrapper);

    const flyingSize = 48;

    /*
     * position: fixed เหมาะกับค่าจาก getBoundingClientRect()
     * จึงไม่ต้องบวก window.scrollY
     */

    const startX =
      imageRect.left +
      imageRect.width / 2 -
      flyingSize / 2;

    const startY =
      imageRect.top +
      imageRect.height / 2 -
      flyingSize / 2;

    const endX =
      cartRect.left +
      cartRect.width / 2 -
      flyingSize / 2;

    const endY =
      cartRect.top +
      cartRect.height / 2 -
      flyingSize / 2;

    flyingWrapper.style.left = `${startX}px`;
    flyingWrapper.style.top = `${startY}px`;

    flyingWrapper.style.setProperty(
      '--moveX',
      `${endX - startX}px`
    );

    flyingWrapper.style.setProperty(
      '--moveY',
      `${endY - startY}px`
    );

    /*
     * รอให้ browser วาดตำแหน่งเริ่มก่อน
     * แล้วจึงเพิ่ม animation class
     */

    window.requestAnimationFrame(() => {
      flyingWrapper.classList.add('fly-animation');
    });

    flyingWrapper.addEventListener(
      'animationend',
      () => {
        flyingWrapper.remove();

        cartIcon.classList.add('cart-bump');

        window.setTimeout(() => {
          cartIcon.classList.remove('cart-bump');
        }, 300);
      },
      {
        once: true
      }
    );

    /*
     * fallback กรณี animationend ไม่ถูกเรียก
     */

    window.setTimeout(() => {
      if (flyingWrapper.isConnected) {
        flyingWrapper.remove();
      }
    }, 1200);
  }

  /**
   * -----------------------------------
   * Global delegated events
   * -----------------------------------
   */

  document.addEventListener('click', (event) => {
    const quickViewButton = event.target.closest(
      '.btn-quick-buy'
    );

    if (quickViewButton) {
      event.preventDefault();
      event.stopPropagation();

      const handle = quickViewButton.dataset.handle;

      if (handle) {
        const { drawer } = getDrawerElements();

        if (drawer) {
          delete drawer.dataset.mode;
          delete drawer.dataset.confirmLabel;
          delete drawer.dataset.studioProductId;
        }

        openQuickView(handle, quickViewButton);
      }

      return;
    }

    const closeButton = event.target.closest('.qv-close');

    if (closeButton) {
      event.preventDefault();
      closeQuickView();
      return;
    }

    const jibbitzButton = event.target.closest(
      '.add-jibbitz'
    );

    if (jibbitzButton) {
      event.preventDefault();
      event.stopPropagation();

      addJibbitz(jibbitzButton);
    }
  });

  /*
   * คลิกพื้นที่ overlay / นอก panel แล้วปิด
   */

  document.addEventListener('mousedown', (event) => {
    const { drawer, panel } = getDrawerElements();

    if (!drawer || !panel) return;
    if (!drawer.classList.contains('is-open')) return;

    if (!panel.contains(event.target)) {
      closeQuickView();
    }
  });

  /*
   * Esc ปิด Drawer
   */

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const { drawer } = getDrawerElements();

    if (drawer?.classList.contains('is-open')) {
      closeQuickView();
    }
  });

  /*
   * เปิดไว้ให้ระบบอื่นเรียกใช้ได้
   * เช่น Jibbitz Drawer ในอนาคต
   */

  window.ProductDrawer = {
    open: openQuickView,
    close: closeQuickView,
    fetchView: fetchProductView,
    clearCache() {
      QUICK_VIEW_CACHE.clear();
    }
  };
})();